import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Calendar, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { content } from '../data';

const TilmeldPage = () => {
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('Tak for din tilmelding! Jeg vender tilbage til dig hurtigst muligt med bekræftelse og detaljer.');
        e.target.reset();
      } else {
        setStatus('Der skete en fejl. Prøv venligst igen senere eller ring direkte.');
      }
    } catch (error) {
      setStatus('Der skete en fejl. Prøv venligst igen senere.');
    }
  };

  return (
    <main className="pt-32 md:pt-56 lg:pt-64">
      <div className="bg-[#04a6df] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-semibold text-white uppercase tracking-tight">
            Tilmeld dig kørekort
          </h1>
          <p className="mt-4 text-white/90 text-lg max-w-2xl mx-auto">
            Udfyld formularen herunder for at tilmelde dig det næste hold eller for at høre mere om dine muligheder.
          </p>
        </div>
      </div>

      <div className="py-24 bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Info Column */}
            <div className="lg:col-span-1 space-y-8">
              <div className="bg-slate-50 p-6 border border-slate-100">
                <h3 className="text-[#034b89] font-semibold uppercase tracking-widest text-sm mb-4">Hvad sker der nu?</h3>
                <ul className="space-y-4">
                  {[
                    "Du modtager en bekræftelse",
                    "Jeg kontakter dig ang. holdstart",
                    "Vi planlægger din første kørelektion",
                    "Du er i gang med dit kørekort!"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-slate-700 text-sm font-medium">
                      <div className="bg-[#04a6df] w-6 h-6 rounded-full flex items-center justify-center shrink-0 shadow-sm">
                        <Check className="text-white" size={14} strokeWidth={4} />
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#034b89] p-6 text-white">
                <h3 className="font-semibold uppercase tracking-widest text-sm mb-4">Har du spørgsmål?</h3>
                <p className="text-sm text-slate-200 mb-4 font-medium italic">
                  "Jeg sidder klar til at hjælpe dig sikkert i mål med dit kørekort."
                </p>
                <div className="space-y-2 text-sm">
                  <p className="font-semibold">Lene Jauert</p>
                  <p>{content.contact.phone}</p>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-2">
              <motion.div 
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white border-2 border-slate-100 p-8 md:p-10 shadow-sm"
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Honeypot field */}
                  <div className="sr-only absolute" aria-hidden="true">
                    <label htmlFor="website_url">Website URL</label>
                    <input type="text" name="website_url" id="website_url" tabIndex="-1" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-900 mb-2">
                        <User size={16} className="text-slate-400" />
                        Navn
                      </label>
                      <input
                        type="text"
                        name="name"
                        id="name"
                        required
                        placeholder="Dit fulde navn"
                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 focus:border-[#04a6df] focus:ring-1 focus:ring-[#04a6df] outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-900 mb-2">
                        <Phone size={16} className="text-slate-400" />
                        Telefon
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        id="phone"
                        required
                        placeholder="Dit telefonnummer"
                        className="w-full px-4 py-4 bg-slate-50 border border-slate-200 focus:border-[#04a6df] focus:ring-1 focus:ring-[#04a6df] outline-none transition-all"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-900 mb-2">
                      <Mail size={16} className="text-slate-400" />
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      required
                      placeholder="Din email adresse"
                      className="w-full px-4 py-4 bg-slate-50 border border-slate-200 focus:border-[#04a6df] focus:ring-1 focus:ring-[#04a6df] outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="start_date" className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-900 mb-2">
                      <Calendar size={16} className="text-slate-400" />
                      Ønsket holdstart (valgfri)
                    </label>
                    <input
                      type="text"
                      name="start_date"
                      id="start_date"
                      placeholder="F.eks. Næste mulige eller specifik måned"
                      className="w-full px-4 py-4 bg-slate-50 border border-slate-200 focus:border-[#04a6df] focus:ring-1 focus:ring-[#04a6df] outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="flex items-center gap-2 text-sm font-medium uppercase tracking-wider text-slate-900 mb-2">
                      <MessageSquare size={16} className="text-slate-400" />
                      Yderligere information
                    </label>
                    <textarea
                      name="message"
                      id="message"
                      rows="4"
                      placeholder="Har du særlige ønsker eller spørgsmål?"
                      className="w-full px-4 py-4 bg-slate-50 border border-slate-200 focus:border-[#04a6df] focus:ring-1 focus:ring-[#04a6df] outline-none transition-all"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#034b89] hover:bg-[#023a6d] text-white font-semibold uppercase tracking-widest py-5 transition-all transform hover:translate-y-[-2px] active:translate-y-[0px] shadow-lg"
                  >
                    Send tilmelding
                  </button>

                  {status && (
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-4 p-4 text-center font-semibold border ${status.includes('Tak') ? 'text-green-700 bg-green-50 border-green-100' : 'text-red-700 bg-red-50 border-red-100'}`}
                    >
                      {status}
                    </motion.div>
                  )}
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
);
};

export default TilmeldPage;
